import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { Autocomplete, Button, Form, Header, Modal, ModalSize, SleekInput } from "@kaladel/react/Components";
import { useValidator } from "@kaladel/react/Hooks";
import { FC, useState } from "react";
import { StaticCurrency } from "ApiClient/models";
import { classNames, Severity } from "@kaladel/react/Utilities";
import { createPortal } from "react-dom";
import modalStyles from "./ModalStyles.module.scss";
import { useApp } from "../AppContext";
import { useApiClient } from "Contexts/ApiClientContext";

interface AddAccountModalProps {
    onClose: (dirty: boolean, newAccountId?: string) => void;
}

export const AddAccountModal: FC<AddAccountModalProps> = (props) => {
    const { apiCall, api } = useApiClient();
    const { currencies } = useApp();
    const validator = useValidator();
    const lastStage = 4;

    const [stage, setStage] = useState<number>(1);

    const [name, setName] = useState<string>("");
    const [currencyId, setCurrencyId] = useState<string>("");
    const [currencyName, setCurrencyName] = useState<string>("");
    const [balance, setBalance] = useState<string>("");
    const [overdraft, setOverdraft] = useState<string>("0");

    const handleBackClicked = () => {
        if (stage !== 1) {
            const nextStage = stage - 1;

            setStage(nextStage);
        }
        else {
            props.onClose(false);
        }
    };

    const handleNextClicked = () => {
        if (!validator.canSubmit()) return;

        if (stage !== lastStage) {
            const nextStage = stage + 1;

            setStage(nextStage);

            validator.reset();
        }
        else {
            saveChanges();
        }
    };

    const getCurrencyAutocompleteResults = async (value: string): Promise<StaticCurrency[]> => {
        if (!currencies?.length) return [];

        return currencies.filter(c => c.code.toLowerCase().includes(value.toLowerCase()) || c.friendlyName.toLowerCase().includes(value.toLowerCase()));
    };

    const handleCurrencySelected = (currency: StaticCurrency) => {
        setCurrencyId(currency.code);
        setCurrencyName(`${currency.code} - ${currency.friendlyName}`);
    };

    const saveChanges = async () => {
        if (!validator.canSubmit()) return;

        const response = await apiCall(() => api.accounts.post({
            name,
            currencyId,
            balance: parseFloat(balance),
            overdraft: parseFloat(overdraft)
        }));

        if (response.kind === "success") {
            props.onClose(true, response.value);
        }
    };

    return (
        createPortal(
            <Modal
                onClose={() => props.onClose(false)}
                size={ModalSize.Medium}
            >
                <Header level={2} title="Add Account" />

                <Form>

                    {stage === 1 && (
                        <>
                            <div className={modalStyles.label}>Give your account a name</div>

                            <SleekInput
                                id="name"
                                label="Name"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                validator={validator}
                                validations={{ required: true }}
                                autoFocus={true}
                            />
                        </>
                    )}

                    {stage === 2 && (
                        <>
                            <div className={modalStyles.label}>What currency is the account in?</div>

                            <Autocomplete<StaticCurrency>
                                autocompleteOptionKey={c => c.code}
                                autocompleteDisplayOption={c => `${c.code} - ${c.friendlyName}`}
                                onAutocomplete={getCurrencyAutocompleteResults}
                                onAutocompleteSelected={handleCurrencySelected}
                                value={currencyName}
                                label="Currency"
                                id="currencyId"
                                validator={validator}
                                validations={{ required: true }}
                                autoFocus={true}
                            />
                        </>
                    )}

                    {stage === 3 && (
                        <>
                            <div className={modalStyles.label}>What is the current balance?</div>

                            <SleekInput
                                id="balance"
                                label="Balance"
                                value={balance}
                                onChange={e => setBalance(e.target.value)}
                                validator={validator}
                                validations={{ required: true, numeric: true }}
                                autoFocus={true}
                            />
                        </>
                    )}

                    {stage === 4 && (
                        <>
                            <div className={modalStyles.label}>What is the overdraft limit? If this account does not have one, leave this as zero.</div>

                            <SleekInput
                                id="overdraft"
                                label="Overdraft"
                                value={overdraft}
                                onChange={e => setOverdraft(e.target.value)}
                                validator={validator}
                                validations={{ required: true, numeric: true }}
                                autoFocus={true}
                            />
                        </>
                    )}

                    <div className={classNames([modalStyles.buttons, modalStyles.right])}>
                        <Button
                            onClick={handleBackClicked}
                            icon={faChevronLeft}
                            iconSide="left"
                            severity={Severity.Tertiary}
                        >
                            {stage !== 1 ? "Back" : "Cancel"}
                        </Button>

                        <Button
                            onClick={handleNextClicked}
                            icon={faChevronRight}
                            iconSide="right"
                            type="submit"
                        >
                            {stage !== lastStage ? "Next" : "Finish"}
                        </Button>
                    </div>
                </Form>

            </Modal>,
            document.body
        )
    );
};